// src/utils/pricing.js
// Shared pricing calculation — used by RegisterPage.jsx and RegistrationsDashboard.jsx

export function toDatePricing(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

/* ------------------------------------------------------------------ */
/* Processing fee (Stripe pass-through)                                 */
/* ------------------------------------------------------------------ */

// Stripe standard: 2.9% + $0.30 per transaction.
export const STRIPE_PCT     = 0.029;   // 2.9%
export const STRIPE_FIXED_C = 30;      // 30 cents

/**
 * Compute the processing fee (in cents) to add on top of `subtotalCents`
 * so that after Stripe's fees across `installments` charges, the coach
 * still nets `subtotalCents`.
 *
 * Formula for N installments: gross = (subtotal + N × fixed) / (1 − pct)
 * For a single charge (pay-in-full), N=1 reduces to the classic
 * gross = (subtotal + 0.30) / (1 − 2.9%).
 *
 * @param {number} subtotalCents  base amount the coach wants to net
 * @param {'absorb' | 'pass_to_parent'} mode
 * @param {number} [installments=1]  number of charges Stripe will process
 * @returns {number} fee to add, in cents (0 when mode === 'absorb')
 */
export function calcProcessingFee(subtotalCents, mode = 'absorb', installments = 1) {
  if (!subtotalCents || subtotalCents <= 0) return 0;
  if (mode !== 'pass_to_parent') return 0;
  const n = Math.max(1, installments | 0);
  const gross = (subtotalCents + n * STRIPE_FIXED_C) / (1 - STRIPE_PCT);
  return Math.ceil(gross - subtotalCents); // round up so coach never undercollects
}

/* ------------------------------------------------------------------ */
/* Payment schedule generator                                           */
/* ------------------------------------------------------------------ */

/**
 * Build the full payment schedule for a registration.
 * First installment is charged at registration (today). Subsequent
 * installments fall on the configured recurring date.
 *
 * @param {object} params
 * @param {number} params.totalCents    total to collect (fee-inclusive)
 * @param {number} params.installments  2..12
 * @param {'weekly'|'biweekly'|'monthly'} [params.frequency='monthly']
 * @param {number} [params.chargeDay]   day of month (1..28) for monthly; ignored for weekly/biweekly
 * @param {Date}   [params.registeredAt=new Date()]  date of first charge
 * @returns {Array<{dueDate: Date, amount: number, status: 'pending'}>}
 */
export function generateSchedule({
  totalCents,
  installments,
  frequency = 'monthly',
  chargeDay,
  registeredAt = new Date(),
}) {
  const n = Math.max(1, installments | 0);
  if (!totalCents || totalCents <= 0 || n === 1) {
    return [{ dueDate: new Date(registeredAt), amount: totalCents || 0, status: 'pending' }];
  }

  // Equal split; first installment absorbs rounding remainder
  const base  = Math.floor(totalCents / n);
  const first = totalCents - base * (n - 1);

  const schedule = [{
    dueDate: new Date(registeredAt),
    amount: first,
    status: 'pending',
  }];

  // Next charge date after `registeredAt`
  let d = computeNextChargeDate(registeredAt, frequency, chargeDay);
  for (let i = 1; i < n; i++) {
    schedule.push({ dueDate: new Date(d), amount: base, status: 'pending' });
    d = advanceDate(d, frequency);
  }
  return schedule;
}

function computeNextChargeDate(from, frequency, chargeDay) {
  const d = new Date(from);
  if (frequency === 'weekly')   { d.setDate(d.getDate() + 7);  return d; }
  if (frequency === 'biweekly') { d.setDate(d.getDate() + 14); return d; }
  // Monthly: next occurrence of chargeDay (clamped to 28 for safety)
  const day = Math.min(Math.max(1, chargeDay || from.getDate()), 28);
  d.setDate(day);
  if (d <= from) d.setMonth(d.getMonth() + 1);
  return d;
}

function advanceDate(d, frequency) {
  const next = new Date(d);
  if (frequency === 'weekly')   next.setDate(next.getDate() + 7);
  else if (frequency === 'biweekly') next.setDate(next.getDate() + 14);
  else                          next.setMonth(next.getMonth() + 1);
  return next;
}

/* ------------------------------------------------------------------ */
/* Main price calculator                                                */
/* ------------------------------------------------------------------ */

/**
 * Calculate the full price breakdown for a registration.
 *
 * @param {object} season  Firestore season document
 * @param {object} inputs  {
 *   division             string | null
 *   hasSibling           boolean
 *   isReturning          boolean
 *   paymentPlan          number | null   (2..12, or null = pay in full)
 *   financialAidDiscount number          cents — coach-applied after the fact (default 0)
 *   registeredAt         Date            for schedule generation (default now)
 * }
 * @returns {object} Full pricing breakdown including paymentPlan.schedule
 */
export function calculatePrice(season, inputs = {}) {
  const now = inputs.registeredAt || new Date();

  // 1. Base amount: division price or flat fee
  let baseAmount = season.registrationFee ?? 0;
  if (season.divisionPricing && inputs.division) {
    const divFee = season.divisionPricing[inputs.division];
    if (divFee != null) baseAmount = divFee;
  }

  // 2. Early bird discount
  let earlyBirdApplied = false;
  let earlyBirdSaving  = 0;
  if (season.earlyBirdEnabled && season.earlyBirdDeadline) {
    const deadline = toDatePricing(season.earlyBirdDeadline);
    if (deadline && now <= deadline) {
      earlyBirdApplied = true;
      earlyBirdSaving  = Math.min(season.earlyBirdDiscount ?? 0, baseAmount);
    }
  }

  // 3. Self-declared discounts
  const siblingDiscount = season.siblingDiscountEnabled && inputs.hasSibling
    ? Math.min(season.siblingDiscountAmount ?? 0, baseAmount)
    : 0;

  const returningDiscount = season.returningDiscountEnabled && inputs.isReturning
    ? Math.min(season.returningDiscountAmount ?? 0, baseAmount)
    : 0;

  // 4. Coach-applied financial aid (default 0 at registration time)
  const financialAidDiscount = inputs.financialAidDiscount ?? 0;

  // 5. Subtotal after discounts
  const totalDiscount  = earlyBirdSaving + siblingDiscount + returningDiscount + financialAidDiscount;
  const subtotalAmount = Math.max(0, baseAmount - totalDiscount);

  // 6. Decide installment count (for fee calculation — fee depends on how many
  //    Stripe transactions will occur)
  const n = typeof inputs.paymentPlan === 'number' ? inputs.paymentPlan : 1;
  const planEnabled = season.paymentPlanEnabled && n > 1 && subtotalAmount > 0;
  const installmentCount = planEnabled ? n : 1;

  // 7. Processing fee — spread across all installments (Byga-style)
  const processingFeeMode = season.processingFeeMode === 'pass_to_parent'
    ? 'pass_to_parent'
    : 'absorb';
  const processingFee = calcProcessingFee(subtotalAmount, processingFeeMode, installmentCount);

  // 8. Final amount the parent will pay (fee-inclusive total)
  const finalAmount = subtotalAmount + processingFee;

  // 9. Payment plan + schedule
  let paymentPlan = null;
  if (planEnabled) {
    const frequency = season.paymentPlanFrequency || 'monthly';
    const chargeDay = season.paymentPlanChargeDay || 15;
    const schedule  = generateSchedule({
      totalCents:   finalAmount,
      installments: n,
      frequency,
      chargeDay,
      registeredAt: now,
    });
    paymentPlan = {
      installments:      n,
      frequency,
      chargeDay,
      firstInstallment:  schedule[0].amount,
      installmentAmount: schedule[1]?.amount ?? schedule[0].amount,
      schedule,
    };
  }

  return {
    baseAmount,
    earlyBirdApplied,
    earlyBirdSaving,
    siblingDiscount,
    returningDiscount,
    financialAidDiscount,
    totalDiscount,
    subtotalAmount,
    processingFeeMode,
    processingFee,
    finalAmount,
    paymentPlan,
  };
}

/** Format cents as $X.XX */
export function fmtMoney(cents) {
  if (!cents) return 'Free';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
